import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  Paper,
  Tabs,
  Tab,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Chip,
  Badge,
  InputBase,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Description as PDFIcon,
  Share as ShareIcon,
  Analytics as AnalyticsIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  ViewList as ListIcon,
  ViewModule as GridIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Components
import PDFList from '../components/pdf/PDFList';
import PDFEditor from '../components/pdf/PDFEditor';
import PDFCategories from '../components/pdf/PDFCategories';
import MainContent from '../components/layout/MainContent';

// Stores
import usePDFStore from '../stores/pdfStore';
import useAuthStore from '../stores/authStore';

const PDFCreator = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Store states
  const {
    pdfs,
    categories,
    loading,
    filters,
    fetchPDFs,
    fetchCategories,
    setFilters,
    clearCurrentPDF
  } = usePDFStore();
  const { user } = useAuthStore();
  
  // Local states
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [isCreating, setIsCreating] = useState(false);
  
  // Load data on mount
  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user) {
      const loadData = async () => {
        try {
          await Promise.all([
            fetchPDFs(),
            fetchCategories()
          ]);
          clearCurrentPDF();
        } catch (error) {
          // Errors already handled in store
          console.error('Error loading PDF data:', error);
        }
      };
      loadData();
    }
  }, [user]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    
    // Update filters based on tab
    switch (newValue) {
      case 0: // All PDFs
        setFilters({ sharedWithMe: false });
        break;
      case 1: // Shared with me
        setFilters({ sharedWithMe: true });
        break;
      case 2: // Categories
        // No filter change needed
        break;
    }
  };
  
  // Handle create new PDF
  const handleCreatePDF = () => {
    setIsCreating(true);
  };
  
  // Handle close editor
  const handleCloseEditor = () => {
    setIsCreating(false);
    fetchPDFs(); // Refresh list
  };
  
  // Render content based on active tab
  const renderContent = () => {
    if (isCreating) {
      return (
        <PDFEditor
          isNew
          onClose={handleCloseEditor}
          onSave={handleCloseEditor}
        />
      );
    }
    
    switch (activeTab) {
      case 0: // All PDFs
      case 1: // Shared with me
        return (
          <PDFList
            viewMode={viewMode}
            onViewModeChange={setViewMode}
          />
        );
      case 2: // Categories
        return <PDFCategories />;
      default:
        return null;
    }
  };
  
  return (
    <MainContent sx={{ backgroundColor: 'background.default', p: 0 }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Ultra Compact Single Header Bar with Everything */}
        <Paper 
          elevation={0} 
          sx={{ 
            borderRadius: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <Box sx={{ 
            px: 2, 
            py: 1,
            display: 'flex', 
            alignItems: 'center',
            gap: 2,
          }}>
            {/* Title and Stats */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                PDFs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ({pdfs.length})
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ width: 1, height: 24, bgcolor: 'divider' }} />
            
            {/* Inline Tabs - No borders */}
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                minHeight: 32,
                '& .MuiTabs-indicator': {
                  height: 2,
                },
                '& .MuiTab-root': {
                  minHeight: 32,
                  minWidth: 60,
                  py: 0,
                  px: 1.5,
                  fontSize: '0.8rem',
                  textTransform: 'none',
                },
              }}
            >
              <Tab label="My PDFs" />
              <Tab label="Shared" />
              <Tab label="Categories" />
            </Tabs>

            {/* Search - Always visible */}
            {!isCreating && (
              <>
                <Box sx={{ width: 1, height: 24, bgcolor: 'divider' }} />
                <Paper
                  elevation={0}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: 200,
                    height: 30,
                    px: 1,
                    backgroundColor: alpha(theme.palette.action.hover, 0.05),
                    border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.action.hover, 0.1),
                    },
                  }}
                >
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
                  <InputBase
                    placeholder="Search..."
                    sx={{ 
                      flex: 1, 
                      fontSize: '0.8rem',
                      '& input': {
                        padding: 0,
                      }
                    }}
                    value={filters.search || ''}
                    onChange={(e) => {
                      setFilters({ search: e.target.value });
                      fetchPDFs();
                    }}
                  />
                </Paper>

                {/* Quick Filters - Inline */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Chip
                    label="Draft"
                    size="small"
                    variant={filters.status === 'draft' ? "filled" : "outlined"}
                    onClick={() => {
                      setFilters({ status: filters.status === 'draft' ? null : 'draft' });
                      fetchPDFs();
                    }}
                    sx={{ 
                      height: 24,
                      fontSize: '0.7rem',
                      borderRadius: 0.75,
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                  <Chip
                    label="Published"
                    size="small"
                    variant={filters.status === 'published' ? "filled" : "outlined"}
                    onClick={() => {
                      setFilters({ status: filters.status === 'published' ? null : 'published' });
                      fetchPDFs();
                    }}
                    sx={{ 
                      height: 24,
                      fontSize: '0.7rem',
                      borderRadius: 0.75,
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                </Box>
              </>
            )}
            
            {/* Spacer */}
            <Box sx={{ flex: 1 }} />
            
            {/* View Toggle - Icon only */}
            {!isCreating && activeTab !== 2 && (
              <Box sx={{ display: 'flex', border: `1px solid ${theme.palette.divider}`, borderRadius: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => setViewMode('grid')}
                  sx={{ 
                    borderRadius: 0.5,
                    p: 0.5,
                    color: viewMode === 'grid' ? 'primary.main' : 'text.secondary',
                    backgroundColor: viewMode === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  }}
                >
                  <GridIcon sx={{ fontSize: 18 }} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => setViewMode('list')}
                  sx={{ 
                    borderRadius: 0.5,
                    p: 0.5,
                    color: viewMode === 'list' ? 'primary.main' : 'text.secondary',
                    backgroundColor: viewMode === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                  }}
                >
                  <ListIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            )}

            {/* Analytics */}
            {activeTab !== 2 && pdfs.length > 0 && (
              <IconButton
                size="small"
                onClick={() => navigate('/pdf/analytics')}
                sx={{ p: 0.5 }}
              >
                <AnalyticsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}

            {/* Create Button */}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={handleCreatePDF}
              sx={{ 
                height: 30,
                textTransform: 'none',
                borderRadius: 1,
                px: 2,
                fontSize: '0.8rem',
              }}
            >
              Create
            </Button>
          </Box>
        </Paper>
        
        {/* Content Area */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {loading && !pdfs.length ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              height: '100%',
            }}>
              <CircularProgress />
            </Box>
          ) : (
            <Fade in timeout={300}>
              <Box sx={{ height: '100%' }}>
                {renderContent()}
              </Box>
            </Fade>
          )}
        </Box>
      </Box>
    </MainContent>
  );
};

export default PDFCreator;